// FeedbackPage — Submit feedback form
// Layout: type picker, rating, message, contact, submit

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TYPES = [
  { key: 'bug',     label: 'Bug report',     emoji: '🐛', sub: 'Something broken' },
  { key: 'feature', label: 'Feature request', emoji: '💡', sub: 'New idea' },
  { key: 'praise',  label: 'What I love',     emoji: '❤️',  sub: 'Positive feedback' },
  { key: 'other',   label: 'Other',           emoji: '💬', sub: 'Anything else' },
] as const;

export function FeedbackPage() {
  const nav = useNavigate();
  const [type, setType] = useState<typeof TYPES[number]['key']>('feature');
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('demo@funderpro.live');
  const [contactOk, setContactOk] = useState(true);

  function submit() {
    if (!message) { alert('Please write something!'); return; }
    alert('Thanks for the feedback! We read every submission.');
    nav(-1);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-32">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Send feedback</div>
        <button onClick={submit} data-testid="submit-feedback" className="px-3 text-[15px] font-semibold text-tf-blue">Send</button>
      </div>

      {/* Type picker */}
      <h2 className="mt-4 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">What's this about?</h2>
      <div className="mx-4 grid grid-cols-2 gap-2" data-testid="feedback-types">
        {TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setType(t.key)}
            data-testid={`fbtype-${t.key}`}
            className={['flex items-center gap-3 rounded-[14px] p-3 text-left transition',
              type === t.key ? 'border-2 border-tf-blue bg-tf-blue-tint' : 'border border-tf-border bg-white active:opacity-40'
            ].join(' ')}
          >
            <div className="text-[24px]">{t.emoji}</div>
            <div>
              <div className="text-[14px] font-semibold text-tf-text-1">{t.label}</div>
              <div className="text-[11px] text-tf-text-3">{t.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Rating */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">How's your experience?</h2>
      <div className="mx-4 rounded-[20px] border border-tf-border bg-white p-5 text-center">
        <div className="flex items-center justify-center gap-1.5" data-testid="rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              data-testid={`rating-${n}`}
              className="active:scale-90 transition"
            >
              <svg viewBox="0 0 24 24" className={['h-9 w-9', n <= rating ? 'text-tf-warning' : 'text-[#E5E5EA]'].join(' ')} fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          ))}
        </div>
        <div className="mt-2 text-[13px] text-tf-text-3">
          {rating === 5 ? 'Loving it! ⭐' : rating === 4 ? 'Pretty good' : rating === 3 ? 'It\'s okay' : rating === 2 ? 'Could be better' : 'Not great'}
        </div>
      </div>

      {/* Message */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Your feedback</h2>
      <div className="mx-4">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          placeholder="Tell us what you think, what's broken, or what you'd love to see..."
          data-testid="feedback-message"
          className="w-full rounded-[12px] border border-tf-border bg-white p-3 text-[14px] text-tf-text-1 focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3"
        />
        <div className="mt-1 text-right text-[10px] text-tf-text-3">{message.length} / 2000</div>
      </div>

      {/* Contact */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Contact</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="px-4 py-2.5 border-b border-tf-border">
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="feedback-email" className="mt-0.5 w-full bg-transparent text-[15px] font-medium text-tf-text-1 focus:outline-none" />
        </div>
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-1">
            <div className="text-[14px] font-medium text-tf-text-1">Follow up via email</div>
            <div className="text-[11px] text-tf-text-3">We'll only contact you about this feedback</div>
          </div>
          <button onClick={() => setContactOk(!contactOk)} data-testid="feedback-contact" className={['relative h-7 w-12 rounded-full transition-colors', contactOk ? 'bg-tf-profit' : 'bg-tf-surface-3'].join(' ')}>
            <div className={['absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all', contactOk ? 'left-[22px]' : 'left-0.5'].join(' ')} />
          </button>
        </div>
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">All feedback is anonymous. We read every submission.</p>
    </div>
  );
}
